import { orderBy } from "@progress/kendo-data-query";

import * as actionTypes from "../constants";
import { DomainType } from "../models";
import { CHOICE_LIST_STATUS } from "../models/cachedChoiceList";

import type { DomainTypeEnum } from "../models";
import type { ChoiceListsState, IdDescriptionBase } from "../models/patientEncounter";

const createEmptyChoiceListsState = (): ChoiceListsState => ({
  facilities: [],
  practices: [],
  customFacilitiesAll: [],
  customFacilities: [],
  encounterTypes: [],
  patientStatuses: [],
  services: [],
  financialClasses: [],
  recordStatuses: [],
  sexes: [],
  ofa: [],
  conversionFactorTypes: [],
  currentEncounterFacility: "",
  currentEncounterFacilities: [],
  newEncounterEncounterTypes: [],
  providers: [],
  cachedLists: {},
  payerFlags: [],
  loading: false,
  loadingNewEncounterFacilities: false,
  loadingDomainList: false,
  loadingNewEncounterEncounterTypes: false,
  loadingCustomFilterFacilities: false,
  billingNotes: [],
  serviceLocations: [],
});

export const choiceListsReducer = (state = createEmptyChoiceListsState(), action) => {
  switch (action.type) {
    case actionTypes.GET_FACILITIES_COMPLETED:
      return handleSetFacilities(state, action.payload);
    case actionTypes.GET_FACILITIES_FAIL:
    case actionTypes.UPDATE_ENCOUNTER_FACILITY_FAIL:
    case actionTypes.GET_CHOICELISTS_FAIL:
      return handleGetFacilitiesFailed(state);
    case actionTypes.GET_CHOICELIST_SUCCESS:
      return handleUpdateSingleChoiceList(state, action.payload);
    case actionTypes.GET_CHOICELIST_FAIL:
      return handleGetSingleChoiceListFail(state, action.payload);
    case actionTypes.UPDATE_ENCOUNTER_FACILITY:
      return handleUpdateChoiceLists(action.payload);
    case actionTypes.SET_CURRENT_ENCOUNTER_FACILITIES:
      return handleSetCurrentEncounterFacilities(state, action.payload);
    case actionTypes.SET_CHOICELISTS_LOADING:
      return handleSetChoiceListsLoading(state, action.payload);

    case actionTypes.CACHED_CHOICELIST_CHANGE_STATUS:
      return handleCachedChoiceChangeStatus(state, action.payload);
    case actionTypes.CACHED_CHOICELIST_LOADED:
      return handleCachedChoiceListLoaded(state, action.payload);

    case actionTypes.GET_CHOICE_LIST_START:
      return handleChoiceListStartLoading(state, action.payload);
    case actionTypes.GET_CHOICE_LIST_END:
      return handleChoiceListEndLoading(state, action.payload);
    case actionTypes.GET_CUSTOM_FACILITIES_COMPLETED:
      return handleSetCustomFacilities(state, action.payload);
    case actionTypes.GET_CUSTOM_FACILITIES_FAIL:
      return handleGetCustomFacilitiesFailed(state);
    default:
      return state;
  }
};

const handleSetCustomFacilities = (
  state: ChoiceListsState,
  payload: {
    facilities: IdDescriptionBase[];
    customAll: IdDescriptionBase[];
    custom: IdDescriptionBase[];
  },
): ChoiceListsState => ({
  ...state,
  facilities: payload.facilities,
  customFacilitiesAll: payload.customAll,
  customFacilities: payload.custom,
});

const handleSetFacilities = (
  state: ChoiceListsState,
  { facilities, domain }: { facilities: IdDescriptionBase[]; domain: DomainTypeEnum },
): ChoiceListsState => {
  const domainInState = domain === DomainType.Practice ? "practices" : "facilities";
  // update state only if we have real changes
  if (
    state[domainInState].length === facilities.length &&
    state.currentEncounterFacilities.length >= facilities.length
  ) {
    let areEqual = true;
    for (let ind = 0, len = facilities.length; ind < len; ind++) {
      if (
        state[domainInState][ind].id !== facilities[ind].id ||
        state[domainInState][ind].title !== facilities[ind].title ||
        state[domainInState][ind].ViewId !== facilities[ind].ViewId ||
        state[domainInState][ind].CurrentUserPermissions !== facilities[ind].CurrentUserPermissions
      ) {
        areEqual = false;
        break;
      }
    }

    if (areEqual) {
      return state;
    }
  }

  // always keep currentEncounterFacility in the currentEncounterFacilities list
  if (!facilities.find((facility) => facility.ViewId === state.currentEncounterFacility)) {
    const currentEncounterFacility = state.currentEncounterFacilities.find(
      (facility) => facility.ViewId === state.currentEncounterFacility,
    );

    if (currentEncounterFacility) {
      const newCurrentEncounterFacilities = [currentEncounterFacility, ...facilities];

      const sortedCurrentEncounterFacilities = orderBy(newCurrentEncounterFacilities, [
        { field: "id", dir: "asc" },
      ]);

      return {
        ...state,
        [domainInState]: facilities,
        currentEncounterFacilities: sortedCurrentEncounterFacilities,
      };
    }
  }

  return {
    ...state,
    [domainInState]: facilities,
    currentEncounterFacilities: facilities,
  };
};

const handleGetFacilitiesFailed = (state: ChoiceListsState): ChoiceListsState => ({
  ...state,
  facilities: [],
  customFacilities: [],
  customFacilitiesAll: [],
  currentEncounterFacilities: [],
  encounterTypes: [],
  patientStatuses: [],
  services: [],
  financialClasses: [],
  recordStatuses: [],
  sexes: [],
  ofa: [],
  providers: [],
  payerFlags: [],
  billingNotes: [],
  serviceLocations: [],
});

const handleGetCustomFacilitiesFailed = (state: ChoiceListsState): ChoiceListsState => ({
  ...state,
  customFacilities: [],
  customFacilitiesAll: [],
});

const handleUpdateChoiceLists = (payload: {
  state: ChoiceListsState;
  choiceLists: ChoiceListsState;
}): ChoiceListsState => ({
  ...payload.state,
  encounterTypes: payload.choiceLists.encounterTypes,
  patientStatuses: payload.choiceLists.patientStatuses,
  services: payload.choiceLists.services,
  financialClasses: payload.choiceLists.financialClasses,
  recordStatuses: payload.choiceLists.recordStatuses,
  sexes: payload.choiceLists.sexes,
  ofa: payload.choiceLists.ofa,
  providers: payload.choiceLists.providers,
  payerFlags: payload.choiceLists.payerFlags,
  billingNotes: payload.choiceLists.billingNotes,
  serviceLocations: payload.choiceLists.serviceLocations,
});

const handleGetSingleChoiceListFail = (
  state: ChoiceListsState,
  name: string,
): ChoiceListsState => ({
  ...state,
  loading: false,
  [name]: [],
});

export const mapChoiceListsToObject = (state, loadedLists) => {
  const newState = state;

  for (let ind = 0, len = loadedLists.length; ind < len; ind++) {
    newState[loadedLists[ind].name] = [...loadedLists[ind].values];
  }
};

const handleUpdateSingleChoiceList = (state: ChoiceListsState, loadedLists): ChoiceListsState => {
  const newState = {
    ...state,
    loading: false,
  };

  mapChoiceListsToObject(newState, loadedLists);

  return newState;
};

const handleCachedChoiceChangeStatus = (
  state: ChoiceListsState,
  payload: { key: string; status: CHOICE_LIST_STATUS },
) => {
  const newState = {
    ...state,
    cachedLists: {
      ...state.cachedLists,
    },
  };

  if (!newState.cachedLists[payload.key]) {
    newState.cachedLists[payload.key] = {
      status: payload.status,
      data: [],
    };
  } else {
    newState.cachedLists[payload.key] = {
      ...newState.cachedLists[payload.key],
      status: payload.status,
    };
  }

  return newState;
};

const handleCachedChoiceListLoaded = (
  state: ChoiceListsState,
  payload: { key: string; data: IdDescriptionBase[] },
) => {
  const newState = {
    ...state,
    cachedLists: {
      ...state.cachedLists,
    },
  };

  newState.cachedLists[payload.key] = {
    status: CHOICE_LIST_STATUS.READY,
    data: payload.data,
  };

  return newState;
};

const handleSetCurrentEncounterFacilities = (
  state: ChoiceListsState,
  payload: { currentEncounterFacility: string; currentEncounterFacilities: IdDescriptionBase[] },
) => ({
  ...state,
  loading: true,
  currentEncounterFacility: payload.currentEncounterFacility,
  currentEncounterFacilities: payload.currentEncounterFacilities,
});

const handleSetChoiceListsLoading = (
  state: ChoiceListsState,
  payload: boolean,
): ChoiceListsState => ({
  ...state,
  loading: payload,
});

const handleChoiceListChangeLoading = (
  state: ChoiceListsState,
  loading: boolean,
  loadingKey?: string,
): ChoiceListsState => {
  if (!loadingKey) {
    return state;
  }

  return {
    ...state,
    [loadingKey]: loading,
  };
};

const handleChoiceListStartLoading = (
  state: ChoiceListsState,
  loadingKey?: string,
): ChoiceListsState => handleChoiceListChangeLoading(state, true, loadingKey);

const handleChoiceListEndLoading = (
  state: ChoiceListsState,
  loadingKey?: string,
): ChoiceListsState => handleChoiceListChangeLoading(state, false, loadingKey);
